import React from 'react';
import { useTranslation } from 'react-i18next';

import { Heading, Snackbar } from '@amzn/storm-ui';
import { pencil } from '@amzn/storm-ui-icons';

import { ErrorDisplay } from '../../../Shared/components/ErrorDisplay/ErrorDisplay';
import ReusableTable from '../../../Shared/components/ReusableTable';
import { ApiError } from '../../../Shared/interfaces/ApiError';
import { CustomCard } from '../../../UiComponentsLibrary/CustomCard';
import { CustomIconButton } from '../../../UiComponentsLibrary/CustomIconButton';
import CustomSpinner from '../../../UiComponentsLibrary/CustomSpinner';
import {
  EXCLUDED_ROLES_COLUMNS,
  ROLE_MANAGEMENT_TABLE_HEADERS_MAP,
} from '../../constants/permissionConstants';
import { Role } from '../../interfaces/RoleManagementInterfaces';
import { EditRole } from '../EditRole';
import { SnackbarContainer } from './RoleManagement.styles';
import { formatCellValue } from '../../utils/helpers';
import { useRoleManagement } from './hooks/useRoleManagement';

const RoleManagement: React.FC = () => {
  const { t } = useTranslation();
  const { rolesQuery, editProps, snackbarProps, selectedArtEventId } = useRoleManagement();
  const { error, isError, isLoading, data: roleData } = rolesQuery;
  const { isEditing, selectedRole, handleEdit, handleEditComplete, handleCancel, editRoleMutation } = editProps;

  const renderRow = (item: Role) => (
    <>
      <td id="role-action-edit-button">
        <CustomIconButton
          icon={pencil}
          label="edit"
          handleOnClickCb={() => handleEdit(item)}
          disabled={!selectedArtEventId}
        />
      </td>
      {Object.entries(item).map(
        ([key, value]) =>
          !EXCLUDED_ROLES_COLUMNS.includes(key) && (
            <td id={`role-${item.userRole}-${key}`} key={key}>
              {formatCellValue(key, value, t)}
            </td>
          )
      )}
    </>
  );

  return (
    <>
      {!isEditing ? (
        <>
          <Heading id="role-management-header" size="medium">
            {t('role-management-header')}
          </Heading>
          <CustomCard title={t('role-management-header')}>
            {isError ? (
              <ErrorDisplay error={error as ApiError} />
            ) : isLoading ? (
              <CustomSpinner />
            ) : !roleData || roleData.roles.length === 0 ? (
              <div>{t('table-no-data-label')}</div>
            ) : (
              <ReusableTable
                tableHeaders={[
                  t('role-management-actions-table-header'),
                  ...Object.keys(roleData.roles[0])
                    .filter((key) => !EXCLUDED_ROLES_COLUMNS.includes(key))
                    .map((key) =>
                      t(
                        ROLE_MANAGEMENT_TABLE_HEADERS_MAP[
                          key as keyof typeof ROLE_MANAGEMENT_TABLE_HEADERS_MAP
                        ]
                      )
                    ),
                ]}
                rowsData={roleData?.roles || []}
                renderRow={renderRow}
                showTotalRow={false}
              />
            )}
            {snackbarProps.editRoleSuccessMessage && (
              <SnackbarContainer>
                <Snackbar variant="success" id="role-edit-success-message">
                  {snackbarProps.editRoleSuccessMessage}
                </Snackbar>
              </SnackbarContainer>
            )}
          </CustomCard>
        </>
      ) : selectedRole ? (
        <>
          <EditRole
            userRole={selectedRole}
            onSubmit={handleEditComplete}
            onCancel={handleCancel}
          />
          {editRoleMutation.isError && (
            <ErrorDisplay error={editRoleMutation.error as ApiError} />
          )}
        </>
      ) : null}
    </>
  );
};

export default RoleManagement;
